import {
  ApiError,
  CancelError,
  LogoService,
  PrintExampleService,
} from '@/api-sdk';
import { uploadService } from '@/context';
import { ApiService, ErrorType, UploadExtensionCheck } from '@/types';
import { ContextProvider } from '@lit/context';
import { LitElement } from 'lit';

const MAX_FILE_SIZE = 104857600;
const INVALID_FILE_EXTENSIONS = /\.(app|exe)$/i;
const PDF_FILE_EXTENSION = /\.pdf$/i;

/**
 * Uploads a file to a specified API endpoint and handles the response and errors.
 *
 * @param {LitElement} context - The context in which the upload is performed.
 * @param {Object} formData - The form data containing the file to be uploaded.
 * @param {ApiService} postApiService - The API service to which the file is uploaded ('logo' or 'printExample').
 * @param {string} customSuccessEvent - The name of the custom event to dispatch on successful upload.
 * @param {string} customErrorEvent - The name of the custom event to dispatch on upload error.
 * @param {string | undefined} locale - Current locale
 */
export const uploadFileToEndpoint = async (
  context: LitElement,
  formData: { file: Blob },
  postApiService: ApiService,
  customSuccessEvent: string,
  customErrorEvent: string,
  locale?: string,
) => {
  let isError = false;

  const uploadServiceContext = new ContextProvider(context, {
    context: uploadService,
    initialValue:
      postApiService === 'logo'
        ? LogoService.postUploadLogo({ locale, formData })
        : PrintExampleService.postUploadPrintExample({ formData }),
  });

  try {
    const response = await uploadServiceContext.value;

    setTimeout(() => {
      if (response) {
        context.dispatchEvent(
          new CustomEvent(customSuccessEvent, {
            composed: true,
            detail: response,
          }),
        );
      }
    }, 1000);
  } catch (error) {
    if (error instanceof CancelError) {
      return;
    }

    if (error instanceof ApiError) {
      context.dispatchEvent(
        new CustomEvent(customErrorEvent, {
          detail: {
            error: error.body.errors[0].detail,
          },
        }),
      );
      return;
    }
    isError = true;
  } finally {
    uploadServiceContext.setValue(undefined);
  }
  if (isError) {
    context.dispatchEvent(
      new CustomEvent(customErrorEvent, {
        detail: {
          error: ErrorType.FILE_UPLOAD,
        },
      }),
    );
  }
};

export const uploadDragOverHandler = (event: DragEvent) => {
  event.preventDefault();
  return true;
};

export const uploadDragEndHandler = (event: DragEvent) => {
  event.preventDefault();
  return true;
};

/**
 * Handles the event when a file is dropped into a designated area.
 *
 * @param {DragEvent} event - The drag event.
 * @param {UploadExtensionCheck} [fileCheck] - Optional file extension check.
 * @returns {Object} - An object containing the file, file name, and any error that occurred.
 */
export const uploadDropHandler = (
  event: DragEvent,
  fileCheck?: UploadExtensionCheck,
):
  | { file: File | null; fileName: string; error: ErrorType | null }
  | undefined => {
  event.preventDefault();
  let error: ErrorType | null = null;

  if (!event?.dataTransfer?.items && event?.dataTransfer?.items.length !== 1) {
    return;
  }

  const data = event.dataTransfer.items[0];

  if (data.kind === 'file') {
    let file = data.getAsFile();

    if (
      (fileCheck === undefined &&
        file &&
        INVALID_FILE_EXTENSIONS.test(file.name)) ||
      (fileCheck === 'pdf' && file && !PDF_FILE_EXTENSION.test(file.name))
    ) {
      error = ErrorType.FILE_EXTENSION;
      file = null;
    }

    if (file && file.size > MAX_FILE_SIZE) {
      error = ErrorType.FILE_SIZE;
      file = null;
    }

    return {
      file: file,
      fileName: file?.name || '',
      error: error,
    };
  }
};

/**
 * Handles the input event when a file is selected through an input element.
 *
 * @param {Event} event - The input event triggered by the file selection.
 * @param {string} [fileCheckExtension] - Optional file extension to check against.
 * @returns {Object} - An object containing the file, file name, and any error that occurred.
 */
export const uploadInputEvent = (
  event: Event,
  fileCheckExtension?: string,
):
  | { file: File | null; fileName: string; error: ErrorType | null }
  | undefined => {
  const input = event.target as HTMLInputElement;
  let error: ErrorType | null = null;

  if (!input.files || !input.files?.length) {
    return;
  }

  let file: File | null = input.files[0];
  if (
    (file && INVALID_FILE_EXTENSIONS.test(file.name)) ||
    (fileCheckExtension &&
      file &&
      !file.name.includes(`.${fileCheckExtension}`))
  ) {
    input.value = '';
    error = ErrorType.FILE_EXTENSION;
    file = null;
  }

  if (file && file.size >= MAX_FILE_SIZE) {
    error = ErrorType.FILE_SIZE;
    file = null;
  }

  return { file: file, fileName: file?.name || '', error: error };
};
